/**
 * Annotations Module
 * Handles clickable annotation/tooltip interactions with smart popover positioning
 * using Floating UI for viewport-aware positioning
 */

import { computePosition, flip, shift, offset } from '@floating-ui/dom'

export default class Annotations {
  constructor(app) {
    this.app = app
    this.annotations = []
    this.currentPopover = null

    this.init()
  }

  init() {
    // Find all annotation elements on the page
    this.annotations = Array.from(document.querySelectorAll('.annotation'))

    if (this.annotations.length === 0) {
      console.log('[Annotations] No annotations found on page')
      return
    }

    // Create the popover element (reusable)
    this.createPopoverElement()

    // Bind click handlers to all annotations
    this.annotations.forEach(annotation => {
      annotation.addEventListener('click', (e) => {
        e.stopPropagation()
        this.handleAnnotationClick(annotation)
      })

      // Make annotations keyboard accessible
      annotation.setAttribute('tabindex', '0')
      annotation.setAttribute('role', 'button')
      annotation.setAttribute('aria-label', 'Show explanation')

      annotation.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          this.handleAnnotationClick(annotation)
        }
      })
    })

    // Close popover when clicking outside
    document.addEventListener('click', () => {
      if (this.currentPopover && this.currentPopover.style.display !== 'none') {
        this.hidePopover()
      }
    })

    // Close popover on Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.currentPopover && this.currentPopover.style.display !== 'none') {
        this.hidePopover()
      }
    })

    console.log('[Annotations] Initialized with', this.annotations.length, 'annotations')
  }

  /**
   * Create the reusable popover element
   */
  createPopoverElement() {
    this.currentPopover = document.createElement('div')
    this.currentPopover.className = 'annotation-popover'
    this.currentPopover.style.display = 'none'

    // Create content container
    const content = document.createElement('div')
    content.className = 'annotation-popover-content'
    this.currentPopover.appendChild(content)

    document.body.appendChild(this.currentPopover)

    // Stop click propagation on popover
    this.currentPopover.addEventListener('click', (e) => {
      e.stopPropagation()
    })
  }

  /**
   * Handle click on an annotation
   * @param {HTMLElement} annotation - The clicked annotation element
   */
  async handleAnnotationClick(annotation) {
    const explanation = annotation.getAttribute('title')

    if (!explanation) {
      console.warn('[Annotations] No explanation found for annotation:', annotation)
      return
    }

    // If clicking the same annotation, toggle it
    if (this.currentPopover.dataset.currentAnnotation === annotation.textContent) {
      this.hidePopover()
      return
    }

    // Update popover content
    const content = this.currentPopover.querySelector('.annotation-popover-content')
    content.textContent = explanation

    // Mark which annotation is currently showing
    this.currentPopover.dataset.currentAnnotation = annotation.textContent

    // Show and position the popover
    this.currentPopover.style.display = 'block'
    await this.positionPopover(annotation)
  }

  /**
   * Position the popover using Floating UI
   * @param {HTMLElement} referenceElement - The annotation element to position relative to
   */
  async positionPopover(referenceElement) {
    const { x, y } = await computePosition(
      referenceElement,
      this.currentPopover,
      {
        placement: 'bottom-start',
        middleware: [
          offset(10), // 10px gap from the reference element
          flip(), // Flip to top if no space at bottom
          shift({ padding: 8 }) // Shift horizontally to stay in viewport
        ]
      }
    )

    // Apply position to popover
    Object.assign(this.currentPopover.style, {
      left: `${x}px`,
      top: `${y}px`,
    })
  }

  /**
   * Hide the popover
   */
  hidePopover() {
    if (this.currentPopover) {
      this.currentPopover.style.display = 'none'
      this.currentPopover.dataset.currentAnnotation = ''
    }
  }

  /**
   * Clean up event listeners (if needed)
   */
  destroy() {
    this.annotations.forEach(annotation => {
      annotation.replaceWith(annotation.cloneNode(true))
    })

    if (this.currentPopover) {
      this.currentPopover.remove()
    }
  }
}
